import { createFileRoute, Link } from '@tanstack/react-router';
import {
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  Divider,
  Group,
  NumberInput,
  Select,
  SimpleGrid,
  Skeleton,
  Stack,
  Switch,
  Table,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { notifications } from '@mantine/notifications';
import { modals } from '@mantine/modals';
import {
  IconAlertCircle,
  IconCheck,
  IconClock,
  IconRefresh,
  IconRobot,
  IconSparkles,
} from '@tabler/icons-react';
import { trpc } from '../../lib/trpc';
import { SecretField } from '../../components/config/SecretField';

type AiProvider = 'gemini' | 'openai' | 'nvidia';
type DayKey = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';

interface DaySchedule {
  start: string;
  end: string;
}

const DAY_LABELS: Array<{ key: DayKey; label: string }> = [
  { key: 'mon', label: 'Monday' },
  { key: 'tue', label: 'Tuesday' },
  { key: 'wed', label: 'Wednesday' },
  { key: 'thu', label: 'Thursday' },
  { key: 'fri', label: 'Friday' },
  { key: 'sat', label: 'Saturday' },
  { key: 'sun', label: 'Sunday' },
];

// Curated model lists for the provider dropdowns. A saved value that isn't in
// the list is still shown (see modelOptions) so older/custom models survive.
const GEMINI_MODELS = [
  'gemini-2.5-pro',
  'gemini-2.5-flash',
  'gemini-2.5-flash-lite',
  'gemini-2.0-flash',
  'gemini-2.0-flash-lite',
  'gemini-1.5-pro',
  'gemini-1.5-flash',
];
const OPENAI_MODELS = [
  'gpt-4o',
  'gpt-4o-mini',
  'gpt-4.1',
  'gpt-4.1-mini',
  'gpt-4.1-nano',
  'o4-mini',
];
const NVIDIA_MODELS = [
  'moonshotai/kimi-k3',
  'nvidia/nemotron-3-ultra-550b-a55b',
  'meta/llama-3.3-70b-instruct',
  'deepseek-ai/deepseek-r1',
];

const TIMEZONES = [
  'UTC',
  'Asia/Kolkata',
  'Asia/Dubai',
  'Asia/Singapore',
  'Asia/Tokyo',
  'Europe/London',
  'Europe/Berlin',
  'America/New_York',
  'America/Chicago',
  'America/Los_Angeles',
  'Australia/Sydney',
];

function AiAndTimingSettings() {
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
      <Stack gap="xl">
        <Stack gap={4}>
          <Group gap="sm">
            <IconSparkles size={24} color="var(--mantine-color-blue-6)" />
            <Title order={2}>AI &amp; timing</Title>
          </Group>
          <Text c="dimmed" size="sm">
            Configure your AI responder and automated reply windows. Use the toggles below to adjust
            each feature.
          </Text>
        </Stack>

        <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="lg">
          <AiSection />
          <BotTimingSection />
        </SimpleGrid>
      </Stack>
    </motion.div>
  );
}

// ── AI Integration ──────────────────────────────────────────────────────

function AiSection() {
  const cfg = trpc.tenancy.ai.getConfig.useQuery();
  const utils = trpc.useUtils();

  const [provider, setProvider] = useState<AiProvider>('gemini');
  const [enabled, setEnabled] = useState(false);
  const [systemPrompt, setSystemPrompt] = useState('');
  const [memoryWindow, setMemoryWindow] = useState(6);
  const [geminiModel, setGeminiModel] = useState('gemini-2.5-flash');
  const [openaiModel, setOpenaiModel] = useState('gpt-4o-mini');
  const [nvidiaModel, setNvidiaModel] = useState('moonshotai/kimi-k3');
  const [keyDraft, setKeyDraft] = useState('');

  // Builds the full setConfig payload from the current form state. `enabled`
  // is passed in so the header toggle can persist on/off independently of the
  // local `enabled` state having flushed yet.
  const buildPayload = (enabledValue: boolean) => ({
    provider,
    enabled: enabledValue,
    systemPrompt: systemPrompt || undefined,
    memoryWindow,
    geminiModel,
    openaiModel,
    nvidiaModel,
    geminiKey: provider === 'gemini' && keyDraft ? keyDraft : undefined,
    openaiKey: provider === 'openai' && keyDraft ? keyDraft : undefined,
    nvidiaKey: provider === 'nvidia' && keyDraft ? keyDraft : undefined,
  });

  const save = trpc.tenancy.ai.setConfig.useMutation({
    onSuccess: () => {
      void utils.tenancy.ai.getConfig.invalidate();
      notifications.show({ message: 'AI settings saved', color: 'green' });
      setKeyDraft('');
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  // The enable/disable switch persists immediately so the choice survives a
  // refresh. Disabling only flips the flag — the provider, model, prompt and
  // API key are all preserved (the backend re-writes them unchanged).
  const toggle = trpc.tenancy.ai.setConfig.useMutation({
    onSuccess: (_d, vars) => {
      void utils.tenancy.ai.getConfig.invalidate();
      notifications.show({
        message: vars.enabled ? 'AI integration enabled' : 'AI integration disabled',
        color: 'green',
      });
    },
    onError: (err, vars) => {
      setEnabled(!vars.enabled); // revert the optimistic flip
      notifications.show({ message: err.message, color: 'red' });
    },
  });

  const reset = trpc.tenancy.ai.resetConfig.useMutation({
    onSuccess: () => {
      void utils.tenancy.ai.getConfig.invalidate();
      setKeyDraft('');
      notifications.show({ message: 'AI configuration reset to defaults', color: 'blue' });
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  const onToggle = (next: boolean) => {
    setEnabled(next); // optimistic — reverted in onError
    toggle.mutate(buildPayload(next));
  };

  const confirmReset = () =>
    modals.openConfirmModal({
      title: 'Reset AI configuration?',
      centered: true,
      children: (
        <Text size="sm">
          This restores the provider, model, system prompt and memory window to their defaults,
          turns the AI responder off, and <b>permanently deletes the saved API key</b>. This cannot
          be undone.
        </Text>
      ),
      labels: { confirm: 'Reset configuration', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () => reset.mutate({}),
    });

  const test = trpc.tenancy.ai.testConnection.useMutation({
    onSuccess: (r) => {
      if (r.ok) {
        notifications.show({
          title: 'AI provider connected',
          message: `${r.provider === 'openai' ? 'OpenAI' : r.provider === 'nvidia' ? 'NVIDIA NIM' : 'Gemini'} responded successfully.`,
          color: 'green',
        });
      } else {
        notifications.show({
          title: 'AI test failed',
          message: r.error,
          color: 'red',
          autoClose: 8000,
        });
      }
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  useEffect(() => {
    if (cfg.data) {
      setProvider(cfg.data.provider);
      setEnabled(cfg.data.enabled);
      setSystemPrompt(cfg.data.systemPrompt ?? '');
      setMemoryWindow(cfg.data.memoryWindow);
      setGeminiModel(cfg.data.geminiModel);
      setOpenaiModel(cfg.data.openaiModel);
      setNvidiaModel(cfg.data.nvidiaModel);
    }
  }, [cfg.data]);

  if (cfg.isPending) return <Skeleton height={400} />;

  const hasKey =
    provider === 'openai'
      ? cfg.data?.hasOpenaiKey
      : provider === 'nvidia'
        ? cfg.data?.hasNvidiaKey
        : cfg.data?.hasGeminiKey;

  return (
    <Card withBorder padding="lg" radius="md" style={{ height: '100%' }}>
      <Stack gap="md">
        <Group justify="space-between" align="center">
          <Group gap="xs">
            <IconRobot size={20} color="var(--mantine-color-blue-6)" />
            <Title order={4}>AI Integration</Title>
          </Group>
          <Switch
            checked={enabled}
            onChange={(e) => onToggle(e.currentTarget.checked)}
            disabled={toggle.isPending}
            size="md"
          />
        </Group>

        <Text c="dimmed" size="xs">
          The AI bot replies when no keyword rule matches.
        </Text>

        <AnimatePresence mode="wait">
          {!enabled ? (
            <motion.div
              key="disabled"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
            >
              <Alert color="gray" variant="light">
                AI integration is currently disabled. Toggle the switch above to configure your AI
                provider.
              </Alert>
            </motion.div>
          ) : (
            <motion.div
              key="enabled"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              style={{ overflow: 'hidden' }}
            >
              <Stack gap="sm">
                <Divider label="Configuration" labelPosition="center" />

                <Select
                  label="AI Provider"
                  placeholder="Select a provider"
                  value={provider}
                  onChange={(v) => setProvider(v as AiProvider)}
                  data={[
                    { value: 'gemini', label: 'Google Gemini' },
                    { value: 'openai', label: 'OpenAI (ChatGPT)' },
                    { value: 'nvidia', label: 'NVIDIA NIM (Kimi-K3 & Open Models)' },
                  ]}
                />

                <SecretField
                  label={
                    provider === 'openai'
                      ? 'OpenAI API key'
                      : provider === 'nvidia'
                        ? 'NVIDIA API key'
                        : 'Gemini API key'
                  }
                  description={
                    hasKey
                      ? 'Stored encrypted. Leave blank to keep existing; click the eye to reveal.'
                      : 'Stored encrypted at rest.'
                  }
                  placeholder="Paste your API key"
                  present={!!hasKey}
                  value={keyDraft}
                  onChange={setKeyDraft}
                  onReveal={() =>
                    utils.tenancy.ai.revealKey.fetch({ provider }).then((r) => r.value)
                  }
                />

                {(() => {
                  const currentModel =
                    provider === 'openai'
                      ? openaiModel
                      : provider === 'nvidia'
                        ? nvidiaModel
                        : geminiModel;
                  const base =
                    provider === 'openai'
                      ? OPENAI_MODELS
                      : provider === 'nvidia'
                        ? NVIDIA_MODELS
                        : GEMINI_MODELS;
                  // Keep a previously-saved custom model visible in the list.
                  const data =
                    currentModel && !base.includes(currentModel) ? [currentModel, ...base] : base;
                  return (
                    <Select
                      label="Model"
                      placeholder="Select a model"
                      searchable
                      allowDeselect={false}
                      data={data}
                      value={currentModel}
                      onChange={(v) => {
                        const val = v ?? '';
                        if (provider === 'openai') setOpenaiModel(val);
                        else if (provider === 'nvidia') setNvidiaModel(val);
                        else setGeminiModel(val);
                      }}
                    />
                  );
                })()}

                <Alert color="blue" variant="light" icon={<IconRobot size={16} />}>
                  The bot&apos;s persona, business context, and knowledge files now live in{' '}
                  <Text component={Link} to="/vendor/settings/ai-agents" fw={600} inherit>
                    AI agents
                  </Text>
                  . Create or edit an agent there — the active agent is used as the auto-reply
                  responder. Provider, API key, model, and memory window below apply as defaults.
                </Alert>

                <NumberInput
                  label="Memory window"
                  description="Number of recent messages to consider (2–20)"
                  min={2}
                  max={20}
                  value={memoryWindow}
                  onChange={(v) => setMemoryWindow(Number(v) || 6)}
                />

                <Group mt="md" grow>
                  <Button
                    variant="light"
                    leftSection={<IconCheck size={16} />}
                    loading={save.isPending}
                    onClick={() => save.mutate(buildPayload(enabled))}
                  >
                    Save AI settings
                  </Button>
                  <Button
                    variant="default"
                    loading={test.isPending}
                    onClick={() => test.mutate({ provider })}
                  >
                    Test connection
                  </Button>
                </Group>
                <Text size="xs" c="dimmed">
                  Test runs a tiny generation against the <b>saved</b> key — save first after
                  rotating it.
                </Text>
              </Stack>
            </motion.div>
          )}
        </AnimatePresence>

        <Divider />
        <Group justify="flex-end">
          <Button
            variant="subtle"
            color="red"
            size="xs"
            leftSection={<IconRefresh size={14} />}
            loading={reset.isPending}
            onClick={confirmReset}
          >
            Reset configuration
          </Button>
        </Group>
      </Stack>
    </Card>
  );
}

// ── Bot Timing ──────────────────────────────────────────────────────────

function BotTimingSection() {
  const cfg = trpc.tenancy.botTiming.getConfig.useQuery();
  const preview = trpc.tenancy.botTiming.previewActive.useQuery(undefined, {
    refetchInterval: 60_000,
  });
  const utils = trpc.useUtils();

  const [enabled, setEnabled] = useState(false);
  const [timezone, setTimezone] = useState('UTC');
  const [schedule, setSchedule] = useState<Record<DayKey, DaySchedule | null>>({
    mon: { start: '09:00', end: '18:00' },
    tue: { start: '09:00', end: '18:00' },
    wed: { start: '09:00', end: '18:00' },
    thu: { start: '09:00', end: '18:00' },
    fri: { start: '09:00', end: '18:00' },
    sat: null,
    sun: null,
  });
  const [afterHours, setAfterHours] = useState('');

  const buildPayload = (enabledValue: boolean) => ({
    enabled: enabledValue,
    timezone,
    schedule,
    afterHoursMessage: afterHours || null,
  });

  const save = trpc.tenancy.botTiming.setConfig.useMutation({
    onSuccess: () => {
      void utils.tenancy.botTiming.getConfig.invalidate();
      void utils.tenancy.botTiming.previewActive.invalidate();
      notifications.show({ message: 'Bot timing saved', color: 'green' });
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  // The restrictions switch persists immediately so the choice survives a
  // refresh. Disabling only flips the flag — the schedule, timezone and
  // after-hours message are preserved (re-written unchanged).
  const toggle = trpc.tenancy.botTiming.setConfig.useMutation({
    onSuccess: (_d, vars) => {
      void utils.tenancy.botTiming.getConfig.invalidate();
      void utils.tenancy.botTiming.previewActive.invalidate();
      notifications.show({
        message: vars.enabled
          ? 'Bot timing restrictions enabled'
          : 'Bot timing restrictions disabled',
        color: 'green',
      });
    },
    onError: (err, vars) => {
      setEnabled(!vars.enabled); // revert the optimistic flip
      notifications.show({ message: err.message, color: 'red' });
    },
  });

  const reset = trpc.tenancy.botTiming.resetConfig.useMutation({
    onSuccess: () => {
      void utils.tenancy.botTiming.getConfig.invalidate();
      void utils.tenancy.botTiming.previewActive.invalidate();
      notifications.show({ message: 'Bot timing reset to defaults', color: 'blue' });
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  const onToggle = (next: boolean) => {
    setEnabled(next); // optimistic — reverted in onError
    toggle.mutate(buildPayload(next));
  };

  const confirmReset = () =>
    modals.openConfirmModal({
      title: 'Reset bot timing?',
      centered: true,
      children: (
        <Text size="sm">
          This restores the default Mon–Fri 09:00–18:00 schedule, clears the timezone and
          after-hours message, and turns restrictions off (the bot replies 24/7 again). This cannot
          be undone.
        </Text>
      ),
      labels: { confirm: 'Reset configuration', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () => reset.mutate({}),
    });

  useEffect(() => {
    if (cfg.data) {
      setEnabled(cfg.data.enabled);
      setTimezone(cfg.data.timezone);
      const merged: Record<DayKey, DaySchedule | null> = {
        mon: null,
        tue: null,
        wed: null,
        thu: null,
        fri: null,
        sat: null,
        sun: null,
      };
      for (const k of Object.keys(merged) as DayKey[]) {
        const v = (cfg.data.schedule as Record<string, DaySchedule | null | undefined>)[k];
        merged[k] = v === null || v === undefined ? null : v;
      }
      setSchedule(merged);
      setAfterHours(cfg.data.afterHoursMessage ?? '');
    }
  }, [cfg.data]);

  if (cfg.isPending) return <Skeleton height={400} />;

  const previewMsg = preview.data
    ? preview.data.active
      ? 'Bot is currently ACTIVE'
      : `Bot is currently INACTIVE${preview.data.reason === 'day_off' ? ' (day off)' : preview.data.reason === 'outside_hours' ? ' (outside hours)' : ''}`
    : '';

  return (
    <Card withBorder padding="lg" radius="md" style={{ height: '100%' }}>
      <Stack gap="md">
        <Group justify="space-between" align="center">
          <Group gap="xs">
            <IconClock size={20} color="var(--mantine-color-blue-6)" />
            <Title order={4}>Bot Timing Restrictions</Title>
          </Group>
          <Switch
            checked={enabled}
            onChange={(e) => onToggle(e.currentTarget.checked)}
            disabled={toggle.isPending}
            size="md"
          />
        </Group>

        <Group justify="space-between" align="center">
          <Text c="dimmed" size="xs">
            Restrict bot replies to specific hours.
          </Text>
          {enabled && preview.data && (
            <Badge variant="dot" size="sm" color={preview.data.active ? 'green' : 'orange'}>
              {preview.data.active ? 'active now' : 'inactive now'}
            </Badge>
          )}
        </Group>

        <AnimatePresence mode="wait">
          {!enabled ? (
            <motion.div
              key="disabled"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
            >
              <Alert color="blue" variant="light" icon={<IconAlertCircle size={16} />}>
                Restrictions are off — the bot will reply 24/7.
              </Alert>
            </motion.div>
          ) : (
            <motion.div
              key="enabled"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              style={{ overflow: 'hidden' }}
            >
              <Stack gap="sm">
                <Divider label="Schedule" labelPosition="center" />

                {previewMsg && (
                  <Text
                    size="xs"
                    fw={500}
                    c={preview.data?.active ? 'green' : 'orange'}
                    ta="center"
                  >
                    {previewMsg}
                  </Text>
                )}

                <Select
                  label="Timezone"
                  data={TIMEZONES}
                  searchable
                  value={timezone}
                  onChange={(v) => setTimezone(v ?? 'UTC')}
                />

                <Table withRowBorders={false} striped verticalSpacing="xs">
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th style={{ fontSize: '10px' }}>Day</Table.Th>
                      <Table.Th style={{ fontSize: '10px' }}>Off</Table.Th>
                      <Table.Th style={{ fontSize: '10px' }}>Start</Table.Th>
                      <Table.Th style={{ fontSize: '10px' }}>End</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {DAY_LABELS.map(({ key, label }) => {
                      const day = schedule[key];
                      const isOff = day === null;
                      return (
                        <Table.Tr key={key}>
                          <Table.Td>
                            <Text size="xs" fw={500}>
                              {label.slice(0, 3)}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Checkbox
                              size="xs"
                              checked={isOff}
                              onChange={(e) =>
                                setSchedule((s) => ({
                                  ...s,
                                  [key]: e.currentTarget.checked
                                    ? null
                                    : { start: '09:00', end: '18:00' },
                                }))
                              }
                            />
                          </Table.Td>
                          <Table.Td>
                            <TextInput
                              size="xs"
                              type="time"
                              disabled={isOff}
                              value={day?.start ?? ''}
                              onChange={(e) =>
                                setSchedule((s) => ({
                                  ...s,
                                  [key]: day
                                    ? { ...day, start: e.currentTarget.value }
                                    : { start: e.currentTarget.value, end: '18:00' },
                                }))
                              }
                            />
                          </Table.Td>
                          <Table.Td>
                            <TextInput
                              size="xs"
                              type="time"
                              disabled={isOff}
                              value={day?.end ?? ''}
                              onChange={(e) =>
                                setSchedule((s) => ({
                                  ...s,
                                  [key]: day
                                    ? { ...day, end: e.currentTarget.value }
                                    : { start: '09:00', end: e.currentTarget.value },
                                }))
                              }
                            />
                          </Table.Td>
                        </Table.Tr>
                      );
                    })}
                  </Table.Tbody>
                </Table>

                <Textarea
                  label="After-hours auto-reply"
                  placeholder="Sent when bot is inactive..."
                  minRows={2}
                  autosize
                  value={afterHours}
                  onChange={(e) => setAfterHours(e.currentTarget.value)}
                  size="xs"
                />

                <Group mt="md">
                  <Button
                    fullWidth
                    variant="light"
                    leftSection={<IconCheck size={16} />}
                    loading={save.isPending}
                    onClick={() => save.mutate(buildPayload(enabled))}
                  >
                    Save bot timing
                  </Button>
                </Group>
              </Stack>
            </motion.div>
          )}
        </AnimatePresence>

        <Divider />
        <Group justify="flex-end">
          <Button
            variant="subtle"
            color="red"
            size="xs"
            leftSection={<IconRefresh size={14} />}
            loading={reset.isPending}
            onClick={confirmReset}
          >
            Reset configuration
          </Button>
        </Group>
      </Stack>
    </Card>
  );
}

export const Route = createFileRoute('/vendor/settings/ai-and-timing')({
  component: AiAndTimingSettings,
});
